import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { FiEdit2, FiTrash2, FiCheck, FiX, FiPlus, FiLoader, FiUserCheck } from 'react-icons/fi';
import { aOffreRh, canEditHrData } from '../../components/Auth/permissions';
import ResponsableDepartement from './ResponsableDepartement';

function DepartmentManager({ departments = [], setDepartments, setDepartmentOptions, onClose, showColumn = true, setShowColumn }) {
    const { t } = useTranslation();
    const [list, setList] = useState(departments || []);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');

    useEffect(() => setList(departments || []), [departments]);

    // Responsable du département (Yros/Kavero#469, T2) : propre à l'offre RH,
    // pour un compte RH ou plus. Sans eux, l'écran reste celui de la Paie.
    const gereResponsables = aOffreRh() && canEditHrData();
    const [responsables, setResponsables] = useState({});
    const [panneauId, setPanneauId] = useState(null);

    const chargerResponsables = async () => {
        try {
            const reponse = await apiService.lireOrganigrammeDepartements();
            if (reponse?.ok) {
                setResponsables(Object.fromEntries(reponse.departements.map(d => [d.id, d])));
            }
        } catch (e) {
            signalerErreur(e, 'DepartmentManager.chargerResponsables');
        }
    };

    useEffect(() => { if (gereResponsables) chargerResponsables(); }, [gereResponsables]);

    const responsableDesigne = (departement) => {
        setResponsables(courant => ({ ...courant, [departement.id]: departement }));
        setPanneauId(null);
        notify(t('organigramme.designe'), '', 2);
    };

    const refresh = async () => {
        try {
            const res = await apiService.getElement('/employee/departments');
            const depts = res.data.departments || [];
            setList(depts);
            if (setDepartments) setDepartments(depts);
            if (setDepartmentOptions) setDepartmentOptions(depts.map(d => ({ value: d.id, label: d.name })));
        } catch (e) {
            signalerErreur(e, 'DepartmentManager.refresh');
            notify(t('departmentManager.refreshError'), '', 3);
        }
    };

    useEffect(() => { refresh(); }, []);

    const handleCreate = async () => {
        const name = input.trim();
        if (!name) return;
        setLoading(true);
        try {
            const res = await apiService.saveElement('/employee/department', { name });
            const newDept = res.data.result || res.data;
            const added = newDept || { id: newDept.id, name: newDept.name };
            await refresh();
            setInput('');
            notify(t('departmentManager.created'), '', 2);
        } catch (e) {
            signalerErreur(e, 'DepartmentManager.handleCreate');
            notify(t('departmentManager.createError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (d) => {
        setEditingId(d.id);
        setEditingName(d.name || '');
    };

    const saveEdit = async (id) => {
        if (!editingName.trim()) return;
        setLoading(true);
        try {
            await apiService.updateElementById('/employee/department', id, { name: editingName });
            await refresh();
            setEditingId(null);
            notify(t('departmentManager.updated'), '', 2);
        } catch (e) {
            signalerErreur(e, 'DepartmentManager.saveEdit');
            notify(t('departmentManager.updateError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('departmentManager.deleteConfirm'))) return;
        setLoading(true);
        try {
            await apiService.updateElementById('/employee/delete-department', id, {});
            await refresh();
            notify(t('departmentManager.deleted'), '', 2);
        } catch (e) {
            signalerErreur(e, 'DepartmentManager.handleDelete');
            notify(t('departmentManager.deleteError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('departmentManager.title')}</h3>
                <div className="flex items-center gap-2">
                    <label className="label cursor-pointer">
                        <span className="label-text mr-2 text-sm">{t('departmentManager.showColumn')}</span>
                        <input
                            type="checkbox"
                            className="toggle toggle-sm checked:bg-[--color-orange]"
                            checked={showColumn}
                            onChange={e => setShowColumn?.(e.target.checked)}
                            aria-label={t('departmentManager.showColumnAria')}
                        />
                    </label>
                    {/* <button className="btn btn-xs btn-outline" onClick={onClose}>Fermer</button> */}
                </div>
            </div>

            <div className="flex gap-2 mb-4">
                <input
                    className="input input-sm input-bordered flex-1"
                    placeholder={t('departmentManager.addPlaceholder')}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                />
                <button className="flex items-center gap-2 px-4 py-2 text-sm bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" onClick={handleCreate} disabled={loading || !input.trim()}>
                    {loading ? <FiLoader className="animate-spin" /> : <FiPlus />} {t('departmentManager.add')}
                </button>
            </div>

            <ul className="divide-y">
                {list.length === 0 && <li className="py-4 text-center text-gray-400">{t('departmentManager.empty')}</li>}
                {list.map(d => (
                    <li key={d.id} className="flex items-center py-2">
                        {editingId === d.id ? (
                            <>
                                <input className="input input-sm input-bordered flex-1 mr-2" value={editingName} onChange={e => setEditingName(e.target.value)} />
                                <button className="btn btn-xs btn-success mr-1" onClick={() => saveEdit(d.id)}><FiCheck /></button>
                                <button className="btn btn-xs btn-outline" onClick={() => setEditingId(null)}><FiX /></button>
                            </>
                        ) : (
                            <div className="flex-1">
                                <div className="flex items-center">
                                    <div className="flex-1 min-w-0">
                                        <div>{d.name}</div>
                                        {gereResponsables && (
                                            <div className="text-xs text-gray-500 truncate">
                                                {responsables[d.id]?.responsable
                                                    ? t('organigramme.responsable', { nom: responsables[d.id].responsable.nom })
                                                    : t('organigramme.aucunResponsable')}
                                            </div>
                                        )}
                                    </div>
                                    {gereResponsables && (
                                        <button className="btn btn-xs btn-outline mr-1"
                                            aria-label={t('organigramme.choisirResponsable')}
                                            title={t('organigramme.choisirResponsable')}
                                            onClick={() => setPanneauId(panneauId === d.id ? null : d.id)}>
                                            <FiUserCheck />
                                        </button>
                                    )}
                                    <button className="btn btn-xs btn-outline mr-1" aria-label={t('departmentManager.rename', { nom: d.name })}
                                        title={t('departmentManager.rename', { nom: d.name })} onClick={() => startEdit(d)}><FiEdit2 /></button>
                                    <button className="btn btn-xs btn-error" aria-label={t('departmentManager.delete', { nom: d.name })}
                                        title={t('departmentManager.delete', { nom: d.name })} onClick={() => handleDelete(d.id)}><FiTrash2 /></button>
                                </div>
                                {panneauId === d.id && (
                                    <ResponsableDepartement
                                        departement={responsables[d.id] || { id: d.id, nom: d.name, responsable: null }}
                                        onDesigne={responsableDesigne}
                                        onFermer={() => setPanneauId(null)}
                                    />
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default DepartmentManager;